<!-- eslint-disable max-lines -->
<template>
  <div v-if="hasChangeAppInfo(packageName)">
    <FormSection
      v-model="editable.basicInfo"
      :title="$t('APPS.INFO.BASIC_INFO')"
      :description="$t('APPS.INFO.BASIC_INFO_DESC')"
      :hint="$t('HINTS.APPS.BASIC_INFO')"
      has-edit
    >
      <Card>
        <ElForm
          ref="basicInfo"
          :model="basicInfoModel"
          label-position="top"
          @submit.native.prevent="updateBasicInfo"
        >
          <template v-if="editable.basicInfo">
            <ElFormItem :label="$t('APPS.CATEGORY')" prop="category">
              <ElSelect
                v-model="basicInfoModel.category"
                class="show-for-medium"
                name="category"
              >
                <ElOption
                  v-for="item in categories"
                  :key="item.id"
                  :label="getCategoryLabel(item)"
                  :value="item.id"
                />
              </ElSelect>

              <select
                v-model="basicInfoModel.category"
                class="hide-for-medium"
                name="category"
              >
                <option
                  v-for="item in categories"
                  :key="item.id"
                  :value="item.id"
                >
                  {{ getCategoryLabel(item) }}
                </option>
              </select>
            </ElFormItem>

            <ElFormItem :label="$t('APPS.PERSIAN_NAME')" prop="name_fa">
              <ElInput
                v-model="basicInfoModel.name_fa"
                name="name_fa"
                dir="rtl"
              />
            </ElFormItem>

            <OptionalFormItem
              prop="name_en"
              :label="$t('APPS.ENGLISH_NAME')"
              :initial-visibility="Boolean(basicInfoModel.name_en)"
              @remove="basicInfoModel.name_en = ''"
            >
              <ElInput
                v-model="basicInfoModel.name_en"
                name="name_en"
                dir="ltr"
                class="text-latin"
              />
            </OptionalFormItem>

            <ElFormItem
              :label="$t('APPS.DESCRIPTION_FA')"
              prop="description_fa"
            >
              <Editor
                v-model="basicInfoModel.description_fa"
                dir="rtl"
                @change="updateDescriptionFA"
              />
            </ElFormItem>

            <OptionalFormItem
              prop="description_en"
              :label="$t('APPS.DESCRIPTION_EN')"
              :initial-visibility="Boolean(basicInfoModel.description_en)"
              @remove="basicInfoModel.description_en = ''"
            >
              <Editor
                v-model="basicInfoModel.description_en"
                dir="ltr"
                class="text-latin"
                @change="updateDescriptionEN"
              />
            </OptionalFormItem>

            <ElFormItem :label="$t('APPS.SHORT_DESCRIPTION_FA')">
              <ElInput
                v-model="basicInfoModel.short_description_fa"
                name="short_description_fa"
                dir="rtl"
                :maxlength="126"
              />
            </ElFormItem>

            <OptionalFormItem
              prop="short_description_en"
              :label="$t('APPS.SHORT_DESCRIPTION_EN')"
              :initial-visibility="Boolean(basicInfoModel.short_description_en)"
              @remove="basicInfoModel.short_description_en = ''"
            >
              <ElInput
                v-model="basicInfoModel.short_description_en"
                name="short_description_en"
                dir="ltr"
                class="text-latin"
                :maxlength="126"
              />
            </OptionalFormItem>

            <ErrorsList :errors="basicInfoFormErrors || []" />

            <ElButton type="primary" native-type="submit" size="large">
              {{ $t('SAVE') }}
            </ElButton>

            <ElButton type="text" @click.native="editable.basicInfo = false">
              {{ $t('CANCEL') }}
            </ElButton>
          </template>

          <template v-else>
            <dl class="list list--definition list--clean">
              <dt>{{ $t('APPS.CATEGORY') }}</dt>
              <dd v-if="categories">
                {{ category }}
              </dd>

              <template v-if="isGame">
                <dt>{{ $t('APPS.INFO.ESRA.AGE_GROUP') }}</dt>
                <dd>
                  <EsraRatingStatus
                    :package-name="packageName"
                    :rating="esraRating"
                    :needs-request="esraNeedsRequest"
                    :is-loading="esraRatingLoading"
                    @refresh="getBasicInfo(packageName)"
                  />
                </dd>
              </template>

              <dt>{{ $t('APPS.PERSIAN_NAME') }}</dt>
              <dd class="text-start" dir="rtl">
                {{ basicInfo.name_fa }}
              </dd>

              <dt>{{ $t('APPS.ENGLISH_NAME') }}</dt>
              <dd
                v-if="basicInfo.name_en"
                class="text-latin text-left"
                dir="ltr"
              >
                {{ basicInfo.name_en }}
              </dd>
              <dd v-else>
                —
              </dd>

              <dt>{{ $t('APPS.DESCRIPTION_FA') }}</dt>
              <dd
                v-if="basicInfo.description_fa"
                v-dompurify-html="basicInfo.description_fa"
                class="text-start app-description-wrapper"
                dir="rtl"
              />
              <dd v-else>
                —
              </dd>

              <dt>{{ $t('APPS.DESCRIPTION_EN') }}</dt>
              <dd
                v-if="basicInfo.description_en"
                v-dompurify-html="basicInfo.description_en"
                class="text-latin text-left app-description-wrapper"
                dir="ltr"
              />
              <dd v-else>
                —
              </dd>

              <dt>{{ $t('APPS.SHORT_DESCRIPTION_FA') }}</dt>
              <dd
                v-dompurify-html="basicInfo.short_description_fa"
                class="text-start"
                dir="rtl"
              />

              <dt>{{ $t('APPS.SHORT_DESCRIPTION_EN') }}</dt>
              <dd
                v-if="basicInfo.short_description_en"
                v-dompurify-html="basicInfo.short_description_en"
                class="text-latin text-left"
                dir="ltr"
              />
              <dd v-else>
                —
              </dd>
            </dl>
          </template>
        </ElForm>
      </Card>
    </FormSection>

    <hr class="divider" />

    <FormSection
      v-model="editable.supportInfo"
      :title="$t('APPS.INFO.SUPPORT_INFO')"
      :description="$t('APPS.INFO.SUPPORT_INFO_DESC')"
      has-edit
    >
      <Card>
        <ElForm
          ref="supportInfo"
          :model="supportInfoModel"
          label-position="top"
          @submit.native.prevent="updateSupportInfo"
        >
          <template v-if="editable.supportInfo">
            <ElFormItem :label="$t('EMAIL')" prop="email">
              <ElInput
                v-model="supportInfoModel.email"
                name="email"
                dir="ltr"
              />
            </ElFormItem>

            <OptionalFormItem
              prop="phone"
              :label="$t('PHONE')"
              :initial-visibility="Boolean(supportInfoModel.phone)"
              @remove="supportInfoModel.phone = ''"
            >
              <ElInput v-model="supportInfoModel.phone" name="phone" />
            </OptionalFormItem>

            <OptionalFormItem
              prop="homepage_fa"
              :label="$t('WEBSITE_FA')"
              :initial-visibility="Boolean(supportInfoModel.homepage_fa)"
              @remove="supportInfoModel.homepage_fa = ''"
            >
              <ElInput
                v-model="supportInfoModel.homepage_fa"
                name="homepage_fa"
                class="text-latin"
                dir="ltr"
              />
            </OptionalFormItem>

            <OptionalFormItem
              prop="homepage_en"
              :label="$t('WEBSITE_EN')"
              :initial-visibility="Boolean(supportInfoModel.homepage_en)"
              @remove="supportInfoModel.homepage_en = ''"
            >
              <ElInput
                v-model="supportInfoModel.homepage_en"
                name="homepage_en"
                class="text-latin"
                dir="ltr"
              />
            </OptionalFormItem>

            <ErrorsList :errors="supportInfoFormErrors" />

            <ElButton type="primary" native-type="submit" size="large">
              {{ $t('SAVE') }}
            </ElButton>

            <ElButton type="text" @click.native="editable.supportInfo = false">
              {{ $t('CANCEL') }}
            </ElButton>
          </template>

          <template v-else>
            <dl class="list list--definition list--clean">
              <dt>{{ $t('EMAIL') }}</dt>
              <dd class="text-latin" dir="ltr">
                {{ supportInfo.email }}
              </dd>

              <dt>{{ $t('PHONE') }}</dt>
              <dd v-if="supportInfo.phone">{{ supportInfo.phone }}</dd>
              <dd v-else>—</dd>

              <dt>{{ $t('WEBSITE_FA') }}</dt>
              <dd v-if="supportInfo.homepage_fa" class="text-latin" dir="ltr">
                {{ supportInfo.homepage_fa }}
              </dd>
              <dd v-else>—</dd>

              <dt>{{ $t('WEBSITE_EN') }}</dt>
              <dd v-if="supportInfo.homepage_en" class="text-latin" dir="ltr">
                {{ supportInfo.homepage_en }}
              </dd>
              <dd v-else>—</dd>
            </dl>
          </template>
        </ElForm>
      </Card>
    </FormSection>

    <hr class="divider" />

    <AppPrice />

    <hr v-if="hasDevelopersSection" class="divider" />

    <FormSection
      v-if="hasDevelopersSection"
      v-model="editable.developer"
      :title="$t('APPS.INFO.DEVELOPER_INFO')"
      :description="$t('APPS.INFO.DEVELOPER_INFO_DESC')"
      has-edit
    >
      <Card>
        <ElForm
          ref="developer"
          label-position="top"
          @submit.native.prevent="updateAuthor"
        >
          <template v-if="editable.developer">
            <ElFormItem :label="$t('APPS.DEVELOPER_TITLE')">
              <ElSelect v-model="appAuthorModel">
                <ElOption
                  v-for="dev in developers"
                  :key="dev.id"
                  :label="getDeveloperLabel(dev)"
                  :value="dev.id"
                />
              </ElSelect>
            </ElFormItem>

            <ErrorsList
              v-if="developerFormErrors"
              :errors="developerFormErrors"
            />

            <ElButton type="primary" native-type="submit" size="large">
              {{ $t('SAVE') }}
            </ElButton>

            <ElButton type="text" @click.native="editable.developer = false">
              {{ $t('CANCEL') }}
            </ElButton>
          </template>

          <template v-else>
            <dl class="list list--definition list--clean">
              <dt>{{ $t('APPS.DEVELOPER_TITLE') }}</dt>
              <dd>{{ currentDeveloperTitle }}</dd>
            </dl>
          </template>
        </ElForm>
      </Card>
    </FormSection>
  </div>
</template>

<script>
import { Apps, DeveloperTitles } from '@/api/api';
import { APP_AUTHOR, APP_CATEGORIES, APP_DETAILS } from '@/store/types';
import { mapActions, mapGetters } from 'vuex';

import Editor from '@@/common/Editor';
import AppPrice from './AppPrice';
import ErrorsList from '@@/common/ErrorsList';
import FormSection from '@@/common/layout/FormSection';
import Card from '@@/common/Card';
import OptionalFormItem from '@@/common/OptionalFormItem';
import EsraRatingStatus from './AppInfo/EsraRatingStatus';

import form from '@/mixins/form';
import permissions from '@/mixins/permissions';
import packageNameWatch from '@/mixins/apps/packageNameWatch';
import packageNameMixin from '@/mixins/packageName';
import lang from '@/mixins/lang';

export default {
  name: 'AppInfo',

  components: {
    Editor,
    AppPrice,
    ErrorsList,
    FormSection,
    Card,
    OptionalFormItem,
    EsraRatingStatus,
  },

  mixins: [form, permissions, packageNameWatch, packageNameMixin, lang],

  data() {
    return {
      developers: [],
      basicInfo: {
        category: 0,
        name_fa: '',
        name_en: '',
        description_fa: '',
        description_en: '',
        short_description_fa: '',
        short_description_en: '',
      },
      basicInfoModel: {
        category: 0,
        name_fa: '',
        name_en: '',
        description_fa: '',
        description_en: '',
        short_description_fa: '',
        short_description_en: '',
      },
      supportInfo: {
        email: '',
        phone: '',
        homepage_fa: '',
        homepage_en: '',
      },
      supportInfoModel: {
        email: '',
        phone: '',
        homepage_fa: '',
        homepage_en: '',
      },
      editable: {
        basicInfo: false,
        supportInfo: false,
        developer: false,
      },
      appAuthorModel: null,
      basicInfoFormErrors: null,
      supportInfoFormErrors: null,
      developerFormErrors: null,
      esraRating: null,
      esraNeedsRequest: false,
      esraRatingLoading: true,
    };
  },

  computed: {
    ...mapGetters({ appAuthor: APP_AUTHOR, app: APP_DETAILS }),

    categories() {
      return this.$store.getters[APP_CATEGORIES];
    },

    category() {
      return this.categories
        .filter(cat => cat.id === this.basicInfo.category)
        .reduce((acc, cat) => this.getCategoryLabel(cat), '');
    },

    currentDeveloperTitle() {
      const currentDeveloper = this.developers.find(
        dev => dev.slug === this.appAuthor?.slug,
      );

      return this.getDeveloperLabel(currentDeveloper);
    },

    hasDevelopersSection() {
      return this.developers.length > 1;
    },

    isGame() {
      const category = (this.categories || []).find(
        item => item.id === this.basicInfo.category,
      );

      if (category) return category.game === true;
      return this.app?.category?.game === true;
    },
  },

  created() {
    this.getInfo();
  },

  methods: {
    ...mapActions({
      getCategories: APP_CATEGORIES,
    }),

    getInfo() {
      const { packageName } = this;

      if (this.hasChangeAppInfo(packageName)) {
        this.getBasicInfo(packageName);
        this.getSupportInfo(packageName);
        if (!this.categories) {
          this.getCategories();
        }
        this.getDevelopers();
      }
    },
    validateEditorInput(changelog) {
      const valueError = this.$t('VALIDATION_REQUIRED_ERR');

      return function validator(rule, value, callback) {
        if (!changelog) {
          callback(new Error(valueError));
        } else {
          callback();
        }
      };
    },

    getDevelopers() {
      return DeveloperTitles.list()
        .then(({ body: { developers = [] } }) => {
          this.developers = developers;
        })
        .catch(({ body: { message } = {} }) => {
          if (message) {
            this.$message.error(message);
          }
        });
    },

    getCategoryLabel(item) {
      if (!item.slug) {
        return '';
      }
      const slug = item.slug.charAt(0).toUpperCase() + item.slug.slice(1);
      if (item.game) {
        return `${item.name} - ${slug} (${this.$t('APPS.GAMES')})`;
      }
      return `${item.name} - ${slug} (${this.$t('APPS.TITLE')})`;
    },

    getDeveloperLabel(dev) {
      if (this.lang !== 'fa') return dev?.nameEn;
      return dev?.nameFa;
    },

    getBasicInfo(packageName) {
      this.esraRatingLoading = true;

      return Apps.getBasicInfo({ packageName })
        .then(
          ({
            body: {
              basic_info: basic,
              esra_rating: esraRating = null,
              esra_needs_request: esraNeedsRequest = false,
            },
          }) => {
            const basicInfo = {
              category: basic.category,
              name_fa: basic.name_fa,
              name_en: basic.name_en,
              description_fa: basic.description_fa,
              description_en: basic.description_en,
              short_description_fa: basic.short_description_fa,
              short_description_en: basic.short_description_en,
            };
            this.basicInfo = Object.assign({}, basicInfo);
            this.basicInfoModel = Object.assign({}, basicInfo);
            this.esraRating = this.normalizeEsraRating(esraRating);
            this.esraNeedsRequest = esraNeedsRequest;
            this.$emit(
              'esra-needs-request-change',
              this.esraNeedsRequest === true,
            );
          },
        )
        .catch(({ body: { message } = {} }) => {
          this.esraRating = null;
          this.esraNeedsRequest = false;
          this.$emit('esra-needs-request-change', false);

          if (message) {
            this.$message.error(message);
          }
        })
        .finally(() => {
          this.esraRatingLoading = false;
        });
    },

    normalizeEsraRating(rating) {
      if (!rating) return null;

      return {
        age: rating.age,
        source: rating.source,
        ageIcon1: rating.age_icon_1,
        ageIcon2: rating.age_icon_2,
      };
    },

    updateBasicInfo() {
      if (!this.isValid('basicInfo')) {
        return false;
      }
      return (
        Apps.updateBasicInfo(
          { packageName: this.packageName },
          this.basicInfoModel,
        )
          .then(({ body: { message, basic_info: basicInfo } }) => {
            this.basicInfo = Object.assign({}, basicInfo);
            this.basicInfoModel = Object.assign({}, basicInfo);

            this.$message.success(message);

            this.basicInfoFormErrors = null;
          })
          // switches to the read-only mode
          .then(() => {
            this.editable.basicInfo = false;
          })
          .catch(({ body: { message, errors } = {} }) => {
            if (message) {
              this.$message.error(message);
            }

            if (errors) {
              this.basicInfoFormErrors = [...errors];
            }
          })
      );
    },

    getSupportInfo(packageName) {
      return Apps.getSupportInfo({ packageName })
        .then(({ body: { support_info: support } }) => {
          const supportInfo = {
            email: support.email,
            phone: support.phone,
            homepage_fa: support.homepage_fa || '',
            homepage_en: support.homepage_en || '',
          };
          this.supportInfo = Object.assign({}, supportInfo);
          this.supportInfoModel = Object.assign({}, supportInfo);
        })
        .catch(({ body: { message } = {} }) => {
          if (message) {
            this.$message.error(message);
          }
        });
    },

    updateSupportInfo() {
      if (!this.isValid('supportInfo')) {
        return false;
      }

      return (
        Apps.updateSupportInfo(
          { packageName: this.$route.params.packageName },
          this.supportInfoModel,
        )
          .then(({ body: { message } }) => {
            this.supportInfo = Object.assign({}, this.supportInfoModel);
            this.$message.success(message);
          })
          // switches to the read-only mode
          .then(() => {
            this.editable.supportInfo = false;
            this.supportInfoFormErrors = null;
          })
          .catch(({ body: { message, errors } = {} }) => {
            if (message) {
              this.$message.error(message);
            }

            if (errors) {
              this.supportInfoFormErrors = [...errors];
            }
          })
      );
    },

    updateAuthor() {
      const author = this.appAuthorModel;

      return Apps.updateAuthor(
        { packageName: this.$route.params.packageName },
        { author },
      )
        .then(({ body: { message } }) => {
          this.$message.success(message);
        })
        .then(() => {
          this.$store.commit(APP_AUTHOR, author);
          this.editable.developer = false;
          this.developerFormErrors = null;
        })
        .catch(({ body: { message, errors } = {} }) => {
          if (message) {
            this.$message.error(message);
          }

          if (errors) {
            this.developerFormErrors = [...errors];
          }
        });
    },

    updateDescriptionFA(value) {
      this.basicInfoModel.description_fa = value;
    },

    updateDescriptionEN(value) {
      this.basicInfoModel.description_en = value;
    },
  },
};
</script>
<style lang="scss">
.app-description-wrapper {
  background: rgba($clouds, 0.4);
  padding: $global-padding;
  font-size: $global-font-size * 0.9;
}
</style>
